import { useState } from 'react';
import CameraCapture, { cameraAvailable } from '../components/CameraCapture';
import type { DayView, MiscRow } from '../lib/day';
import { dayApi, sectionEditable } from '../lib/day';
import { formatDate, formatRs } from '../lib/money';
import { compressPhoto } from '../lib/photo';
import ReceiptImage from '../components/ReceiptImage';
import CostCodeSelect from '../components/CostCodeSelect';
import type { CostCode } from '../lib/costcodes';
import { codeOf } from '../lib/costcodes';

/**
 * Misc expenses. "Every expense opens as its own card — an Add expense button,
 * and tapping a row reopens it."
 *
 * Plant categories (tyre repair, spare parts, repairs and servicing, vehicle
 * fuel) must be allocated to a plant; saving is blocked otherwise and any item
 * missing one is flagged red. Which categories need a plant is owner-managed
 * data now, not a hard-coded list — the flag rides on the category.
 */

interface Plant { id: string; code: string; name: string; cost_code_id?: string | null }
interface Category { id: string; name: string; requires_plant: number }

interface Props {
  view: DayView;
  plants: Plant[];
  categories: Category[];
  costCodes: CostCode[];
  isOwner: boolean;
  onChanged: () => void;
  onError: (message: string) => void;
}

export default function Misc({
  view, plants, categories, costCodes, isOwner, onChanged, onError,
}: Props) {
  const editable = sectionEditable(view, 'misc', isOwner);
  const [openId, setOpenId] = useState<string | 'new' | null>(null);

  const plantOf = (id: string | null) => plants.find((plant) => plant.id === id) ?? null;

  return (
    <div className="card">
      <div className="row" style={{ paddingTop: 0 }}>
        <div>
          <h3>Misc expenses — {formatDate(view.day.workDate)}</h3>
          <div className="sub">
            {view.misc.length} items · {formatRs(view.totals.miscRs)} · tap one to open it
          </div>
        </div>
        {editable && (
          <button type="button" className="btn pri" onClick={() => setOpenId('new')}>
            Add expense
          </button>
        )}
      </div>

      {openId === 'new' && (
        <ExpenseCard
          expense={null}
          plants={plants}
          categories={categories}
          costCodes={costCodes}
          onCancel={() => setOpenId(null)}
          onSave={async (body, photo) => {
            const saved = await dayApi.addMisc(view.day.workDate, body);
            // The photo taken on the new form follows the expense it belongs
            // to; the outbox sends them in that order when there's no signal.
            if (photo) {
              await dayApi.saveReceipt(view.day.workDate, saved.id, await compressPhoto(photo));
            }
            // Closed once saved. Reopening it showed a second Save, and the
            // office took that to mean the first hadn't worked (21 Oct 2026).
            setOpenId(null);
            onChanged();
          }}
          onDelete={null}
          onReceipt={null}
          onError={onError}
        />
      )}

      {view.misc.length === 0 && openId !== 'new' && (
        <div className="empty">Nothing entered yet.</div>
      )}

      {view.misc.map((expense) => {
        const plant = plantOf(expense.plant_id);
        // Booked to a plant removed since: still a plant, named by the Worker.
        const removedCode = !plant && expense.plant_id && expense.plant_code ? expense.plant_code : null;
        const missingPlant = expense.requires_plant === 1 && !plant && !removedCode;
        return (
          <div key={expense.id}>
            <div
              className="row tap"
              role="button"
              tabIndex={0}
              onClick={() => setOpenId(openId === expense.id ? null : expense.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setOpenId(openId === expense.id ? null : expense.id);
                }
              }}
            >
              <div style={{ flex: 1 }}>
                <div className="name">{expense.description}</div>
                <div className="sub">
                  {expense.category_name}
                  {plant ? (
                    <> · <span className="pill p-info">{plant.code}</span></>
                  ) : removedCode ? (
                    <> · <span className="pill">{removedCode} · removed</span></>
                  ) : missingPlant ? (
                    <> · <span className="pill p-no">no plant chosen</span></>
                  ) : null}
                  {codeOf(costCodes, expense.cost_code_id) && (
                    <> · <span className="pill">{codeOf(costCodes, expense.cost_code_id)}</span></>
                  )}
                  {' · '}
                  {expense.receipt_key ? 'receipt attached' : 'no receipt'}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="name">{formatRs(expense.amount_rs)}</span>
                <span className="sub" style={{ fontSize: 18, color: 'var(--text-3)' }}>
                  {openId === expense.id ? '⌄' : '›'}
                </span>
              </div>
            </div>

            {openId === expense.id && editable && (
              <ExpenseCard
                expense={expense}
                plants={plants}
                categories={categories}
                costCodes={costCodes}
                onCancel={() => setOpenId(null)}
                onSave={async (body) => {
                  await dayApi.updateMisc(view.day.workDate, expense.id, {
                    ...body, rowVersion: expense.row_version,
                  });
                  setOpenId(null);
                  onChanged();
                }}
                onDelete={async () => {
                  await dayApi.deleteMisc(view.day.workDate, expense.id);
                  setOpenId(null);
                  onChanged();
                }}
                onReceipt={async (file) => {
                  await dayApi.saveReceipt(view.day.workDate, expense.id, await compressPhoto(file));
                  onChanged();
                }}
                onError={onError}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------- one card

interface CardProps {
  expense: MiscRow | null;
  plants: Plant[];
  categories: Category[];
  costCodes: CostCode[];
  onCancel: () => void;
  onSave: (body: {
    description: string; amountRs: number; categoryId: string; plantId: string | null;
    costCodeId: string | null;
  }, photo: File | null) => Promise<void>;
  onDelete: (() => Promise<void>) | null;
  /** Null on a new expense: its photo is held and sent with the Save. */
  onReceipt: ((file: File) => Promise<void>) | null;
  onError: (message: string) => void;
}

function ExpenseCard({
  expense, plants, categories, costCodes, onCancel, onSave, onDelete, onReceipt, onError,
}: CardProps) {
  const [description, setDescription] = useState(expense?.description ?? '');
  const [amount, setAmount] = useState(expense ? String(expense.amount_rs) : '');
  const [categoryId, setCategoryId] = useState(expense?.category_id ?? categories[0]?.id ?? '');
  const [plantId, setPlantId] = useState(expense?.plant_id ?? '');
  const [costCodeId, setCostCodeId] = useState<string | null>(expense?.cost_code_id ?? null);
  // Required once the owner has set any up; until then there is nothing to pick.
  const codesInUse = costCodes.some((code) => code.is_active);
  const codeMissing = codesInUse && !costCodeId;
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);

  const [camera, setCamera] = useState(false);
  // A new expense's photo, waiting for Save.
  const [heldPhoto, setHeldPhoto] = useState<File | null>(null);

  async function attach(file: File | undefined) {
    if (!file) return;
    setCamera(false);
    if (!onReceipt) {
      setHeldPhoto(file);
      return;
    }
    setPhotoBusy(true);
    try {
      await onReceipt(file);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save that photo.');
    } finally {
      setPhotoBusy(false);
    }
  }

  const category = categories.find((row) => row.id === categoryId) ?? null;
  const needsPlant = category?.requires_plant === 1;
  const plantMissing = needsPlant && !plantId;

  async function save() {
    setProblem('');
    if (!description.trim()) {
      setProblem('Say what the expense was for.');
      return;
    }
    const amountRs = Math.trunc(Number(amount) || 0);
    if (amountRs <= 0) {
      setProblem('Enter the amount.');
      return;
    }
    if (plantMissing) {
      setProblem(
        `${category?.name} has to be allocated to a plant. Choose which machine it was for.`,
      );
      return;
    }
    if (codeMissing) {
      setProblem('Choose a cost code for the expense.');
      return;
    }

    setBusy(true);
    try {
      await onSave({
        description: description.trim(),
        amountRs,
        categoryId,
        plantId: plantId || null,
        costCodeId,
      }, heldPhoto);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save that expense.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        background: 'var(--surface-2)', borderRadius: 12,
        padding: '12px 14px', margin: '0 -4px 8px',
      }}
    >
      <div style={{ fontWeight: 500, marginBottom: 8 }}>
        {expense ? 'Edit expense' : 'New expense'}
      </div>

      <input
        type="text"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="What was it for?"
      />

      <div className="bar" style={{ marginTop: 8 }}>
        <input
          type="number"
          min="0"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Amount"
          style={{ textAlign: 'right' }}
        />
        <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          {categories.map((row) => (
            <option key={row.id} value={row.id}>
              {row.name}
              {row.requires_plant ? ' (plant)' : ''}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginTop: 8 }}>
        <div className="sub" style={needsPlant ? { color: 'var(--text-danger)' } : undefined}>
          {needsPlant
            ? `Which plant is this for? Required for ${category?.name.toLowerCase()}.`
            : 'Plant (optional)'}
        </div>
        <select
          value={plantId}
          onChange={(e) => {
            setPlantId(e.target.value);
            // Choosing a plant fills in its usual code; it can still be changed.
            const usual = plants.find((plant) => plant.id === e.target.value)?.cost_code_id;
            if (usual) setCostCodeId(usual);
          }}
          style={plantMissing ? { outline: '2px solid var(--danger)' } : undefined}
        >
          <option value="">Not for a plant (general)</option>
          {plants.map((plant) => (
            <option key={plant.id} value={plant.id}>
              {plant.code} — {plant.name}
            </option>
          ))}
        </select>
      </div>

      {codesInUse && (
        <div style={{ marginTop: 8 }}>
          <div className="sub" style={codeMissing ? { color: 'var(--text-danger)' } : undefined}>
            {codeMissing ? 'Cost code — required' : 'Cost code'}
          </div>
          <div style={codeMissing ? { outline: '2px solid var(--danger)', borderRadius: 'var(--radius)' } : undefined}>
            <CostCodeSelect codes={costCodes} value={costCodeId} onChange={setCostCodeId} />
          </div>
        </div>
      )}

      {problem && <div className="err">{problem}</div>}

      <div className="bar" style={{ marginTop: 8 }}>
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        {onDelete && (
          <button
            type="button"
            className="btn dang"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void onDelete().catch((err: unknown) =>
                onError(err instanceof Error ? err.message : 'Could not remove that.'),
              ).finally(() => setBusy(false));
            }}
          >
            Delete
          </button>
        )}
        <button type="button" className="btn pri" onClick={() => void save()} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>

      {/* The receipt. Compressed on the tablet to a JPEG under ~300KB, sent to
          R2 by way of the Worker, and queued like any other write when there
          is no signal. */}
      <div style={{ marginTop: 12 }}>
        <div className="sub">Receipt</div>
        {expense?.receipt_key === 'queued' ? (
          <div className="sub" style={{ marginTop: 4 }}>
            Photo taken — it will be sent when the connection comes back.
          </div>
        ) : expense?.receipt_key ? (
          <ReceiptImage id={expense.id} />
        ) : heldPhoto ? (
          <div className="sub" style={{ marginTop: 4 }}>
            Photo taken — it is sent when you save.
          </div>
        ) : null}
        {/* The app opens the camera itself: a Surface Pro's browser turns a
            capture= file input into a file picker. The file input stays as
            the way in when there is no camera to open. */}
        {cameraAvailable() ? (
          <button type="button" className="btn" style={{ marginTop: 8 }} disabled={photoBusy} onClick={() => setCamera(true)}>
            {photoBusy ? 'Saving photo…' : expense?.receipt_key || heldPhoto ? 'Take the photo again' : 'Take a photo of the receipt'}
          </button>
        ) : (
          <label className="btn" style={{ marginTop: 8, cursor: 'pointer' }}>
            {photoBusy ? 'Saving photo…' : expense?.receipt_key || heldPhoto ? 'Take the photo again' : 'Take a photo of the receipt'}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              disabled={photoBusy}
              style={{ display: 'none' }}
              onChange={(e) => {
                void attach(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>
        )}
        {camera && (
          <CameraCapture
            title="Photo of the receipt"
            onCapture={(file) => void attach(file)}
            onCancel={() => setCamera(false)}
          />
        )}
      </div>
    </div>
  );
}
